/**
 * this file is for storing methods to communicate back and forth
 * between the webpage and native app (android & ios).
 */

import {removeOffer} from '~/CreateFabricInvitation/api/review';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {REVERT_TO_BASIC} from '~/common/fabric/reducers/fabricDesign/constants';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {
  selectCharityID,
  selectCrowdfundPurpose,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {PLAN_NAME_PARTY_PASS, PLAN_NAME_PRO} from '~/common/fabric/routes/EventReview/constants';
import {runWebviewToAppBridge} from '~/common/utils/webview';

export function webViewOnNav(newStep, nextStep, prevStep, nextStepLabel) {
  runWebviewToAppBridge(
    'fabricCreateOnNav',
    newStep || '',
    nextStep || '',
    prevStep || '',
    nextStepLabel || ''
  );
}

export function webViewSetBusy(busy) {
  runWebviewToAppBridge('fabricCreateSetBusy', busy);
}

export function webViewSetEventId(eventId) {
  runWebviewToAppBridge('fabricCreateSetEventId', eventId);
}

export function webViewSetIsPremium(isPremium) {
  runWebviewToAppBridge('fabricCreateIsPremium', isPremium);
}

export function webViewPendingUpgradeToPremium(status) {
  runWebviewToAppBridge('pendingUpgradeToPremium', status);
}

export function webViewSetDowngradeSaveStatus(statusCode) {
  runWebviewToAppBridge('setDowngradeSaveStatus', statusCode);
}

export function webViewSetPostCheckoutSaveStatus(statusCode) {
  runWebviewToAppBridge('setPostCheckoutSaveStatus', statusCode);
}

export function webViewSetSendCompleteStatus(status) {
  runWebviewToAppBridge('setSendCompleteStatus', status);
}

export const setupWebviewDowngradeConnection = (store, onSave) => {
  window.webview = window.webview || {};
  window.downgradePremium = async () => {
    store.dispatch({
      type: REVERT_TO_BASIC,
    });
    store.dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {isPremium: false, hasUpgraded: false},
    });
    let statusCode;
    try {
      await removeOffer(store);
      const state = store.getState();
      const {user} = state;
      const charityID = selectCharityID(state);
      const crowdFundPurpose = selectCrowdfundPurpose(state);
      const registries = state.registry?.registries || [];
      if (
        user?.subscriptionPlanName !== PLAN_NAME_PRO &&
        user?.subscriptionPlanName !== PLAN_NAME_PARTY_PASS &&
        !charityID &&
        !crowdFundPurpose &&
        registries.length === 0
      ) {
        // If free template w/o upgrade and is draft event (these can be assumed true here since Downgrade is only available when this is the case)
        // and no subscription, and no other gift options, restore Gift Ideas and save the change
        store.dispatch({
          type: UPDATE_GIFT_OPTIONS,
          payload: {
            options: {
              // biome-ignore lint/style/useNamingConvention: existing error
              event_option_show_gifting: true,
            },
          },
        });
      }
      const resp = await onSave();
      webViewPendingUpgradeToPremium(false);
      statusCode = resp?.status || 412;
      // biome-ignore lint/correctness/noUnusedVariables: existing error
    } catch (e) {
      statusCode = 400; // Some other error not caught; just return 400 error
    }
    webViewSetDowngradeSaveStatus(statusCode);
  };
};

export const setupWebviewCheckoutCompleteConnection = (store, onSave) => {
  window.webview = window.webview || {};
  window.checkoutComplete = async () => {
    let statusCode;
    try {
      const resp = await onSave(true);
      statusCode = resp?.status || 412;
      // biome-ignore lint/correctness/noUnusedVariables: existing error
    } catch (e) {
      statusCode = 400; // Some other error not caught; just return 400 error
    }
    webViewSetPostCheckoutSaveStatus(statusCode);
  };
};

export const setupWebviewSendCompleteConnection = (store) => {
  window.webview = window.webview || {};
  window.sendComplete = () => {
    try {
      /**
       * NOTE: We may want to do a fresh pull of the event object here, to avoid potentially making
       * saving changes based on an outdated version of the event object.
       * saveEventThunk writes to the backend on top of returning the latest state of the event,
       * which is more than we want here, so we may need an API call to just return the latest
       * state of the event object.
       */
      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {inEditMode: true},
      });
      // Send apps status of changing flow into edit flow
      webViewSetSendCompleteStatus(true);

      return true;
      // biome-ignore lint/correctness/noUnusedVariables: existing error
    } catch (e) {
      // Something failed; cannot assume webview is safely in edit flow now
      webViewSetSendCompleteStatus(false);
      return false;
    }
  };
};

export const setupWebviewUpsellAcceptConnection = (store) => {
  window.webview = window.webview || {};
  window.acceptPostSendUpsell = async () => {
    try {
      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {
          isPremium: true,
          isUpsellMode: true,
          preventUpdateModal: true,
        },
      });
      webViewPendingUpgradeToPremium(true);
      return true;
      // biome-ignore lint/correctness/noUnusedVariables: existing error
    } catch (err) {
      return false;
    }
  };
};
